import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiGet, apiPost, apiPut } from '../services/api';
import './InstaladoresPage.css';

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000';

const STATUS = {
  pendiente:  { label: 'Pendiente',  cls: 'ip-st--pend' },
  confirmada: { label: 'Confirmada', cls: 'ip-st--conf' },
  reagendada: { label: 'Reagendada', cls: 'ip-st--reag' },
  en_curso:   { label: 'En curso',   cls: 'ip-st--curso' },
  completada: { label: 'Completada', cls: 'ip-st--done' },
};
const ACCESS_LABEL = { facil: 'Fácil', dificil: 'Difícil', grua: 'Requiere grúa' };

const parseDT = s => (s ? new Date(String(s).replace(' ', 'T')) : null);
const fmtDT = s => { const d = parseDT(s); return d ? d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'; };
const fmtTime = s => { const d = parseDT(s); return d ? d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : ''; };
const mapsUrl = (la, lo) => `https://www.google.com/maps?q=${la},${lo}`;
const pad = n => String(n).padStart(2, '0');
const toInputDT = s => { const d = parseDT(s); return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` : ''; };
const fromInputDT = v => (v ? v.replace('T', ' ') + ':00' : null);

// Agrupa archivos por carpeta, igual que la app
function groupFiles(files, isVisit, total) {
  if (isVisit) return [
    { key: 'croquis', label: 'Croquis', items: files.filter(f => f.type === 'croquis') },
    { key: 'zona', label: 'Fotos de la zona', items: files.filter(f => f.type !== 'croquis') },
  ].filter(g => g.items.length);
  const by = files.reduce((a, f) => { const d = Number(f.day_number) || 0; (a[d] = a[d] || []).push(f); return a; }, {});
  return Object.entries(by).sort((a, b) => a[0] - b[0]).map(([d, items]) => ({
    key: d, items,
    label: Number(d) === 0 ? 'Otras fotos' : (Number(d) === total ? `Final de obra · día ${d}` : `Día ${d}`),
  }));
}

// ── Fila de trabajo expandible ────────────────────────────────────────────────
function JobRow({ job, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const isVisit = job.kind === 'visit';
  const base = isVisit ? '/visits' : '/installations';
  const st = STATUS[job.status] || { label: job.status, cls: '' };
  const total = Number(job.duration_days) || 1;
  const done  = Number(job.days_done) || 0;

  const [dateVal, setDateVal] = useState(toInputDT(job.scheduled_at));
  const [editDate, setEditDate] = useState(false);

  async function decideReschedule(decision) {
    setBusy(true);
    try { await apiPost(`${base}/${job.id}/reschedule/${decision}`, {}, token); onChanged && onChanged(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  async function saveDate() {
    setBusy(true);
    try { await apiPut(`${base}/${job.id}`, { scheduled_at: fromInputDT(dateVal) }, token); setEditDate(false); onChanged && onChanged(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  async function toggle() {
    const n = !open; setOpen(n);
    if (n && sessions === null) {
      try { setSessions(await apiGet(`${base}/${job.id}/sessions`, token)); } catch { setSessions([]); }
      const q = isVisit ? `visit_id=${job.id}` : `installation_id=${job.id}`;
      try { setFiles(await apiGet(`/installer-files?${q}`, token)); } catch {}
    }
  }

  const accessType  = isVisit ? job.access_type  : job.visit_access_type;
  const accessNotes = isVisit ? job.access_notes : job.visit_access_notes;

  return (
    <div className={`ip-job ${open ? 'ip-job--open' : ''}`}>
      <button className="ip-job-head" onClick={toggle}>
        <span className={`ip-kind ip-kind--${job.kind}`}>{isVisit ? 'Visita' : 'Instalación'}</span>
        <div className="ip-job-main">
          <div className="ip-job-name">{job.customer_name || 'Cliente'}</div>
          <div className="ip-job-meta">
            {job.installer_name || 'Sin instalador'} · 🗓 {fmtDT(job.scheduled_at)}
            {!isVisit && total > 1 && ` · ${done}/${total} días`}
          </div>
        </div>
        {job.reschedule_at && <span className="ip-resched-chip">⏳ Reagendar</span>}
        <span className={`ip-st ${st.cls}`}>{st.label}</span>
        <span className="ip-caret">{open ? '▾' : '▸'}</span>
      </button>

      {job.reschedule_at && (
        <div className="ip-resched">
          <div>⏳ <b>Reagendado solicitado</b> por {job.installer_name || 'el instalador'}: nueva fecha <b>{fmtDT(job.reschedule_at)}</b>{job.reschedule_reason ? ` — ${job.reschedule_reason}` : ''}</div>
          <div className="ip-resched-btns">
            <button className="ip-btn ip-btn--ok" disabled={busy} onClick={() => decideReschedule('approve')}>Aceptar</button>
            <button className="ip-btn ip-btn--no" disabled={busy} onClick={() => decideReschedule('reject')}>Rechazar</button>
          </div>
        </div>
      )}

      {open && (
        <div className="ip-job-body">
          <div className="ip-grid">
            {job.address && <div><span>Dirección</span><b>{job.address}</b></div>}
            {job.customer_phone && <div><span>Teléfono</span><b>{job.customer_phone}</b></div>}
            {!isVisit && Number(job.price) > 0 && <div><span>Precio</span><b>{Number(job.price).toLocaleString('es-ES', { minimumFractionDigits: 2 })} €</b></div>}
            {accessType && <div><span>Acceso {isVisit ? '' : '(visita)'}</span><b>{ACCESS_LABEL[accessType] || accessType}</b></div>}
            {accessNotes && <div><span>Notas acceso</span><b>{accessNotes}</b></div>}
          </div>

          {/* Editar fecha directamente (admin) */}
          <div className="ip-datebar">
            {editDate ? (
              <>
                <input type="datetime-local" value={dateVal} onChange={e => setDateVal(e.target.value)} />
                <button className="ip-btn ip-btn--ok" disabled={busy || !dateVal} onClick={saveDate}>Guardar fecha</button>
                <button className="ip-btn ip-btn--no" disabled={busy} onClick={() => { setEditDate(false); setDateVal(toInputDT(job.scheduled_at)); }}>Cancelar</button>
                <span className="ip-muted">El instalador recibirá aviso del cambio.</span>
              </>
            ) : (
              job.status !== 'completada' && <button className="ip-btn ip-btn--edit" onClick={() => setEditDate(true)}>🗓 Cambiar fecha</button>
            )}
          </div>

          <div className="ip-sub-ttl">Partes de trabajo</div>
          {sessions === null ? <div className="ip-muted">Cargando…</div>
            : sessions.length === 0 ? <div className="ip-muted">Aún no ha iniciado el trabajo.</div>
            : (
              <table className="ip-ses">
                <thead><tr><th>Día</th><th>Inicio</th><th>Ubic.</th><th>Fin</th><th>Ubic.</th><th>Resumen del día</th></tr></thead>
                <tbody>
                  {sessions.map(s => (
                    <tr key={s.id}>
                      <td>{s.day_number}</td>
                      <td>{fmtTime(s.started_at)}</td>
                      <td>{s.start_lat != null ? <a href={mapsUrl(s.start_lat, s.start_lng)} target="_blank" rel="noreferrer">📍 ver</a> : '—'}</td>
                      <td>{s.finished_at ? fmtTime(s.finished_at) : <em className="ip-live">en curso…</em>}</td>
                      <td>{s.finish_lat != null ? <a href={mapsUrl(s.finish_lat, s.finish_lng)} target="_blank" rel="noreferrer">📍 ver</a> : '—'}</td>
                      <td>{s.summary || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          {files.length > 0 && (
            <>
              <div className="ip-sub-ttl">Fotos ({files.length})</div>
              {groupFiles(files, isVisit, total).map(g => (
                <div key={g.key} className="ip-photo-group">
                  <div className="ip-photo-glbl">{g.label} ({g.items.length})</div>
                  <div className="ip-thumbs">
                    {g.items.map(f => (
                      <a key={f.id} href={API_ORIGIN + f.url} target="_blank" rel="noreferrer" className="ip-thumb">
                        <img src={API_ORIGIN + f.url} alt={f.filename} />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────
export default function InstaladoresPage() {
  const { auth } = useContext(AuthContext);
  const token = auth.token;
  const [installers, setInstallers] = useState([]);
  const [jobs, setJobs] = useState(null);
  const [fInstaller, setFInstaller] = useState('');
  const [fType, setFType] = useState('');
  const [manageOpen, setManageOpen] = useState(false);

  const loadInstallers = useCallback(() => {
    apiGet('/installers', token).then(d => setInstallers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);
  useEffect(() => { loadInstallers(); }, [loadInstallers]);

  const load = useCallback(async () => {
    setJobs(null);
    const p = fInstaller ? `?installer_id=${fInstaller}` : '';
    try {
      const [v, n] = await Promise.all([apiGet('/visits' + p, token), apiGet('/installations' + p, token)]);
      const all = [...(v || []).map(x => ({ ...x, kind: 'visit' })), ...(n || []).map(x => ({ ...x, kind: 'installation' }))]
        .sort((a, b) => (parseDT(b.scheduled_at) || 0) - (parseDT(a.scheduled_at) || 0));
      setJobs(all);
    } catch { setJobs([]); }
  }, [token, fInstaller]);
  useEffect(() => { load(); }, [load]);

  const shown = (jobs || []).filter(j => !fType || j.kind === fType);
  const active = shown.filter(j => j.status !== 'completada').length;

  return (
    <div className="ip-page">
      <div className="ip-header">
        <div>
          <h1 className="ip-title">Instaladores</h1>
          <p className="ip-sub">{shown.length} trabajos · {active} activos</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ip-refresh" onClick={() => setManageOpen(true)}>👷 Gestionar instaladores</button>
          <button className="ip-refresh" onClick={load}>↻ Actualizar</button>
        </div>
      </div>

      <div className="ip-filters">
        <select value={fInstaller} onChange={e => setFInstaller(e.target.value)}>
          <option value="">Todos los instaladores</option>
          {installers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select value={fType} onChange={e => setFType(e.target.value)}>
          <option value="">Visitas e instalaciones</option>
          <option value="visit">Solo visitas</option>
          <option value="installation">Solo instalaciones</option>
        </select>
      </div>

      {jobs === null ? <div className="ip-empty">Cargando…</div>
        : shown.length === 0 ? <div className="ip-empty">No hay trabajos.</div>
        : <div className="ip-list">{shown.map(j => <JobRow key={j.kind + j.id} job={j} token={token} onChanged={load} />)}</div>}

      {manageOpen && (
        <InstallersManager
          token={token}
          installers={installers}
          onChanged={loadInstallers}
          onClose={() => setManageOpen(false)}
        />
      )}
    </div>
  );
}

// ── Gestión de instaladores (alta / edición / activar) ────────────────────────
const EMPTY_INSTALLER = { id: null, name: '', phone: '', email: '', password: '' };

function InstallersManager({ token, installers, onChanged, onClose }) {
  const [form, setForm] = useState(EMPTY_INSTALLER);
  const [showAccess, setShowAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const editing = !!form.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function startNew() { setForm(EMPTY_INSTALLER); setShowAccess(false); setErr(''); }
  function startEdit(inst) {
    setForm({ id: inst.id, name: inst.name || '', phone: inst.phone || '', email: inst.email || '', password: '' });
    setShowAccess(!!inst.email);
    setErr('');
  }

  async function save() {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setBusy(true); setErr('');
    try {
      const body = { name: form.name.trim(), phone: form.phone.trim() };
      if (form.email.trim()) body.email = form.email.trim();
      if (form.password)     body.password = form.password;
      if (editing) await apiPut(`/installers/${form.id}`, body, token);
      else         await apiPost('/installers', body, token);
      startNew();
      onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  async function toggleActive(inst) {
    setBusy(true); setErr('');
    try { await apiPut(`/installers/${inst.id}`, { active: inst.active ? 0 : 1 }, token); onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const ovl = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, zIndex: 1000, overflowY: 'auto' };
  const panel = { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,.25)' };
  const inp = { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 10, boxSizing: 'border-box' };
  const lbl = { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 };
  const btn = (bg, c) => ({ background: bg, color: c, border: c === '#fff' ? 'none' : `1px solid ${c}`, borderRadius: 8, padding: '9px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' });

  return (
    <div style={ovl} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Gestionar instaladores</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
        </div>

        {/* Formulario alta / edición */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16, background: '#f8fafc' }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{editing ? 'Editar instalador' : 'Nuevo instalador'}</div>
          <label style={lbl}>Nombre *</label>
          <input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre del instalador" />
          <label style={lbl}>Teléfono</label>
          <input style={inp} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Teléfono de contacto" />

          {!showAccess ? (
            <button type="button" onClick={() => setShowAccess(true)} style={{ ...btn('#eef2ff', '#4f46e5'), marginBottom: 4 }}>
              {editing ? '🔑 Cambiar / dar acceso a la app' : '🔑 Dar acceso a la app (opcional)'}
            </button>
          ) : (
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: 10, marginTop: 4 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Datos para que el instalador entre en la app. Déjalo vacío si solo quieres tenerlo en el listado.</div>
              <label style={lbl}>Email de acceso</label>
              <input style={inp} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@ejemplo.com" />
              <label style={lbl}>Contraseña {editing && <span style={{ fontWeight: 400, color: '#94a3b8' }}>(dejar vacío = no cambiar)</span>}</label>
              <input style={inp} type="text" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Mínimo 6 caracteres" />
            </div>
          )}

          {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '4px 0 10px' }}>{err}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={busy} style={btn('#16a34a', '#fff')}>{busy ? '…' : (editing ? 'Guardar cambios' : 'Dar de alta')}</button>
            {editing && <button onClick={startNew} disabled={busy} style={btn('#fff', '#64748b')}>Cancelar</button>}
          </div>
        </div>

        {/* Listado */}
        <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 8 }}>Instaladores ({installers.length})</div>
        {installers.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: 13 }}>Aún no hay instaladores. Da de alta el primero arriba.</p>
        ) : installers.map(inst => (
          <div key={inst.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {inst.name} {!inst.active && <span style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>· INACTIVO</span>}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                {inst.phone || 'Sin teléfono'} · {inst.user_id ? '✅ acceso a la app' : '— sin acceso'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button onClick={() => startEdit(inst)} disabled={busy} style={btn('#fff', '#4f46e5')}>Editar</button>
              <button onClick={() => toggleActive(inst)} disabled={busy} style={btn('#fff', inst.active ? '#b91c1c' : '#16a34a')}>{inst.active ? 'Desactivar' : 'Activar'}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
